// frontend/src/components/common/GlobalSearch.js
// Header search: lets any team member look up the status of a Part or JIRA
// Ticket, including ones assigned to other engineers. Unlike Parts Tracker /
// JIRA Tickets (scoped to "my own work"), results here are unrestricted --
// but only status/stepper/history is shown (via the unscoped /workflow
// endpoints, see WorkflowPreview below), never business data like customer,
// hours or emails -- that still requires being the assignee/QC/manager.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    IconButton, Tooltip, Popover, Box, TextField, InputAdornment,
    List, ListItemButton, ListItemText, Chip, Typography, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../services/api';
import { STATUS_COLORS } from '../../constants/domain';
import StatusFlowPanel from './StatusFlowPanel';
import {
    getPartWorkflow, getTicketWorkflow, getPartHistory, getTicketHistory,
} from '../../services/endpoints';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : null);

/**
 * Read-only preview for a search result -- deliberately built on the
 * unscoped /workflow endpoints (minimal, non-sensitive fields) rather than
 * the full part/ticket record, since a search result can belong to anyone.
 * No move/approve/reject controls: this is a status check, not an edit
 * surface (that still requires being the assignee/QC/manager, enforced
 * server-side on the full detail and action endpoints).
 */
const WorkflowPreview = ({ kind, id }) => {
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const load = kind === 'part' ? getPartWorkflow(id) : getTicketWorkflow(id);
        load.then(res => setItem(res.data))
            .catch(() => setItem(null))
            .finally(() => setLoading(false));
    }, [kind, id]);

    if (loading) {
        return (
            <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }
    if (!item) {
        return <Typography color="text.secondary">Could not load this record.</Typography>;
    }

    return (
        <StatusFlowPanel
            item={item}
            label={kind === 'part' ? 'Part' : 'Ticket'}
            readOnly
            onLoadHistory={() => (kind === 'part' ? getPartHistory(id) : getTicketHistory(id))
                .then(r => r.data).catch(() => [])}
        />
    );
};

const ResultRow = ({ kind, keyLabel, title, status, engineerName, dueDate, onClick }) => (
    <ListItemButton sx={{ alignItems: 'flex-start' }} onClick={onClick}>
        <ListItemText
            primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label={kind === 'part' ? 'Part' : 'Ticket'}
                        size="small"
                        color={kind === 'part' ? 'primary' : 'secondary'}
                        variant="outlined"
                    />
                    <Typography variant="body2" fontWeight={700}>{keyLabel}</Typography>
                    <Chip
                        label={status}
                        size="small"
                        sx={{
                            ml: 'auto',
                            bgcolor: STATUS_COLORS[status] || 'grey.400',
                            color: '#fff',
                        }}
                    />
                </Box>
            }
            secondary={
                <Typography variant="caption" color="text.secondary" component="span">
                    {title}
                    {' — '}
                    {engineerName ? `Engineer: ${engineerName}` : 'Unassigned'}
                    {dueDate ? ` • Due ${dueDate}` : ''}
                </Typography>
            }
        />
    </ListItemButton>
);

const GlobalSearch = () => {
    const [anchor, setAnchor] = useState(null);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({ parts: [], tickets: [] });
    const [loading, setLoading] = useState(false);
    const [flowTarget, setFlowTarget] = useState(null); // { kind, id, keyLabel } | null
    const debounceRef = useRef(null);

    const runSearch = useCallback((q) => {
        if (q.trim().length < 2) {
            setResults({ parts: [], tickets: [] });
            setLoading(false);
            return;
        }
        setLoading(true);
        api.get(`/search?q=${encodeURIComponent(q.trim())}`)
            .then(res => setResults(res.data || { parts: [], tickets: [] }))
            .catch(() => setResults({ parts: [], tickets: [] }))
            .finally(() => setLoading(false));
    }, []);

    const openSearch = (event) => setAnchor(event.currentTarget);
    const closeSearch = () => {
        setAnchor(null);
        setQuery('');
        setResults({ parts: [], tickets: [] });
    };

    const onChange = (event) => {
        const value = event.target.value;
        setQuery(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(value), 300);
    };

    // A result is status-only here (no edit access implied) -- opening it
    // shows a read-only workflow preview (current stage, stepper, history),
    // never the full record. See WorkflowPreview.
    const openFlow = (target) => {
        setFlowTarget(target);
        closeSearch();
    };
    const closeFlow = () => setFlowTarget(null);

    const hasQuery = query.trim().length >= 2;
    const totalResults = results.parts.length + results.tickets.length;

    return (
        <>
            <Tooltip title="Search parts & tickets">
                <IconButton color="inherit" onClick={openSearch}>
                    <SearchIcon />
                </IconButton>
            </Tooltip>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={closeSearch}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 420, maxHeight: 480 } } }}
            >
                <Box sx={{ p: 1.5 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Part number, ticket key, description..."
                        value={query}
                        onChange={onChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: loading ? <CircularProgress size={16} /> : null,
                        }}
                    />
                </Box>
                <Divider />

                {!hasQuery && (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Search across every part and JIRA ticket, including ones
                            assigned to other engineers -- shows status only.
                        </Typography>
                    </Box>
                )}

                {hasQuery && !loading && totalResults === 0 && (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            No matches for "{query}"
                        </Typography>
                    </Box>
                )}

                {hasQuery && totalResults > 0 && (
                    <List dense sx={{ maxHeight: 380, overflowY: 'auto' }}>
                        {results.parts.map(p => (
                            <ResultRow
                                key={`part-${p.id}`}
                                kind="part"
                                keyLabel={p.part_number}
                                title={p.part_name}
                                status={p.status}
                                engineerName={p.assigned_to_name}
                                dueDate={fmtDate(p.due_date)}
                                onClick={() => openFlow({ kind: 'part', id: p.id, keyLabel: p.part_number })}
                            />
                        ))}
                        {results.tickets.map(t => (
                            <ResultRow
                                key={`ticket-${t.id}`}
                                kind="ticket"
                                keyLabel={t.ticket_key}
                                title={t.type_of_activity}
                                status={t.status}
                                engineerName={t.assigned_to_name}
                                dueDate={fmtDate(t.due_date)}
                                onClick={() => openFlow({ kind: 'ticket', id: t.id, keyLabel: t.ticket_key })}
                            />
                        ))}
                    </List>
                )}
            </Popover>

            <Dialog open={!!flowTarget} onClose={closeFlow} maxWidth="md" fullWidth>
                <DialogTitle>Workflow status — {flowTarget?.keyLabel}</DialogTitle>
                <Divider />
                <DialogContent>
                    {flowTarget && <WorkflowPreview kind={flowTarget.kind} id={flowTarget.id} />}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default GlobalSearch;
